import { Injectable } from '@angular/core';
import { StrtPurposeMissionBeliefs } from '../models/plan/StrtPurposeMissionBeliefs';
import { BehaviorSubject, Observable } from 'rxjs/index';
import { StrtPriority } from '../models/plan/StrtPriority';
import { StrtInitiative } from '../models/plan/StrtInitiative';
import { DataUtils } from '../utilities/data-utils';
import { StrtObjective } from '../models/plan/StrtObjective';
import { StrtKeyMeasure } from '../models/plan/StrtKeyMeasure';
import { StrtOutcome } from '../models/plan/StrtOutcome';
import { StrtHasFlags } from '../models/ui/StrtHasFlags';
import { IAnnualPlanPriority } from '../../strategies/services/IAnnualPlanPriority';
import { StrtActivity } from '../models/plan/StrtActivity';
import {StrtAssumption} from '../models/plan/StrtAssumption';
import {StrtInfluentialFactor} from '../models/plan/StrtInfluentialFactor';

@Injectable({
  providedIn: 'root'
})
export class StrategiesDataStoreService {

  readonly defaultHasFlags = {
    hasPriorities: false,
    hasObjectives: false,
    hasInitiatives: false,
    hasKeyMeasures: false
  };

  private _purposeMissionBeliefsBS: BehaviorSubject<StrtPurposeMissionBeliefs|undefined> =
    new BehaviorSubject<StrtPurposeMissionBeliefs|undefined>(undefined);
  purposeMissionBeliefs$ = this._purposeMissionBeliefsBS.asObservable();

  private _prioritiesBS: BehaviorSubject<StrtPriority[]> = new BehaviorSubject<StrtPriority[]>([]);
  priorities$ = this._prioritiesBS.asObservable();

  private _hasFlagsBS: BehaviorSubject<StrtHasFlags> = new BehaviorSubject<StrtHasFlags>(this.defaultHasFlags);
  hasFlags$ = this._hasFlagsBS.asObservable();

  private _datedHasFlagsBS: BehaviorSubject<StrtHasFlags> = new BehaviorSubject<StrtHasFlags>(this.defaultHasFlags);
  datedHasFlags$ = this._datedHasFlagsBS.asObservable();

  private _AIPPrioritiesBS: BehaviorSubject<IAnnualPlanPriority[]> = new BehaviorSubject<IAnnualPlanPriority[]>([]);
  AIPPriorities$ = this._AIPPrioritiesBS.asObservable();

  private _datedInitiativesWithActivitiesSubject: BehaviorSubject<StrtPriority[]> = new BehaviorSubject<StrtPriority[]>([]);
  datedInitiativesWithActivities$: Observable<StrtPriority[]> = this._datedInitiativesWithActivitiesSubject.asObservable();

  private _impactStatement: BehaviorSubject<string> = new BehaviorSubject<string>('');
  impactStatement$: Observable<string> = this._impactStatement.asObservable();

  private _toaAutoSelectInitiativeBS: BehaviorSubject<StrtInitiative|null> = new BehaviorSubject<StrtInitiative>(null);
  toaAutoSelectInitiative$: Observable<StrtInitiative|null> = this._toaAutoSelectInitiativeBS.asObservable();


  constructor() {
    this.priorities$.subscribe(this.updateHasFlags.bind(this));
    this.datedInitiativesWithActivities$.subscribe(this.updateDatedHasFlags.bind(this));
  }

  public reset() {
    this._prioritiesBS.next([]);
    this._AIPPrioritiesBS.next([]);
    this._datedInitiativesWithActivitiesSubject.next([]);
    this._impactStatement.next('');
  }

  updateHasFlags = (priorities: StrtPriority[]): void => {
    const hasFlags: StrtHasFlags = this.calcHasFlags(priorities);
    this._hasFlagsBS.next(hasFlags);
  }

  updateDatedHasFlags = (priorities: StrtPriority[]): void => {
    const hasFlags: StrtHasFlags = this.calcHasFlags(priorities);
    this._datedHasFlagsBS.next(hasFlags);
  }

  calcHasFlags = (priorities: StrtPriority[]): StrtHasFlags => {
    const hasFlags: StrtHasFlags = JSON.parse(JSON.stringify(this.defaultHasFlags));
    hasFlags.hasPriorities = priorities.length > 0;
    priorities.map((priority: StrtPriority) => {
      hasFlags.hasObjectives = hasFlags.hasObjectives || priority.objectives.length > 0;
      priority.objectives.map((objective: StrtObjective) => {
        hasFlags.hasInitiatives = hasFlags.hasInitiatives || objective.initiatives.length > 0;
        objective.initiatives.map((initiative: StrtInitiative) => {
          hasFlags.hasKeyMeasures = hasFlags.hasKeyMeasures || initiative.keyMeasures.length > 0;
        });
      });
    });
    return hasFlags;
  }

  public set priorities(priorities: StrtPriority[]) {
    this._prioritiesBS.next(priorities);
  }

  public set datedInitiativesWithActivities(priorities: StrtPriority[]) {
    this._datedInitiativesWithActivitiesSubject.next(priorities);
  }

  public set impactStatement(impactStmt: string) {
    this._impactStatement.next(impactStmt);
  }

  public set purposeMissionBeliefs(pmb: StrtPurposeMissionBeliefs) {
    this._purposeMissionBeliefsBS.next(pmb);
  }

  public set AIPPriorities(priorities: IAnnualPlanPriority[]) {
    this._AIPPrioritiesBS.next(priorities);
  }

  public set toaAutoSelectInitiative(initiative: StrtInitiative) {
    this._toaAutoSelectInitiativeBS.next(initiative);
  }

  public updateInitiative(initiative: StrtInitiative, priorities: StrtPriority[]) {
    DataUtils.create(priorities).reconcileInitiative(initiative);
    this._prioritiesBS.next(priorities);
  }

  public updateObjective(objective: StrtObjective, priorities: StrtPriority[]) {
    DataUtils.create(priorities).reconcileObjective(objective);
    this._prioritiesBS.next(priorities);
  }

  public updateKeyMeasure(keyMeasure: StrtKeyMeasure, priorities: StrtPriority[]) {
    DataUtils.create(priorities).reconcileKeyMeasure(keyMeasure);
    this._prioritiesBS.next(priorities);
  }

  public updateKeyMeasures(keyMeasures: StrtKeyMeasure[], priorities: StrtPriority[]) {
    DataUtils.create(priorities).reconcileKeyMeasures(keyMeasures);
    this._prioritiesBS.next(priorities);
  }

  public updateOutcome(outcome: StrtOutcome, priorities: StrtPriority[]) {
    DataUtils.create(priorities).reconcileOutcome(outcome);
    this._prioritiesBS.next(priorities);
  }

  public updateActivity(activity: StrtActivity, priorities: StrtPriority[]) {
    DataUtils.create(priorities).reconcileActivity(activity);
    this._datedInitiativesWithActivitiesSubject.next(priorities);
  }

  public updateOutcomes(outcomes: StrtOutcome[], priorities: StrtPriority[]) {
    DataUtils.create(priorities).reconcileOutcomes(outcomes);
    this._prioritiesBS.next(priorities);
  }

  public addKeyMeasures(keyMeasures: StrtKeyMeasure[], priorities: StrtPriority[]) {
    DataUtils.create(priorities).addKeyMeasures(keyMeasures);
    this._prioritiesBS.next(priorities);
  }

  public deleteKeyMeasure(keyMeasure: StrtKeyMeasure, priorities: StrtPriority[]) {
    DataUtils.create(priorities).deleteKeyMeasure(keyMeasure, keyMeasure.initiativeId);
    this._prioritiesBS.next(priorities);
  }

  public deleteKeyMeasureFromAll(keyMeasure: StrtKeyMeasure, priorities: StrtPriority[]) {
    DataUtils.create(priorities).deleteKeyMeasureFromAll(keyMeasure);
    this._prioritiesBS.next(priorities);
  }

  public editKeyMeasure(keyMeasure: StrtKeyMeasure, priorities: StrtPriority[]) {
    DataUtils.create(priorities).editKeyMeasure(keyMeasure);
    this._prioritiesBS.next(priorities);
  }

  public addAssumptions(assumptions: StrtAssumption[], priorities: StrtPriority[]) {
    DataUtils.create(priorities).addAssumptions(assumptions);
    this._prioritiesBS.next(priorities);
  }

  public updateAssumption(assumption: StrtAssumption, priorities: StrtPriority[]) {
    DataUtils.create(priorities).reconcileAssumption(assumption);
    this._prioritiesBS.next(priorities);
  }

  public addInfluentialFactors(influentialFactors: StrtInfluentialFactor[], priorities: StrtPriority[]) {
    DataUtils.create(priorities).addInfluentialFactors(influentialFactors);
    this._prioritiesBS.next(priorities);
  }

  public updateInfluentialFactor(influentialFactor: StrtInfluentialFactor, priorities: StrtPriority[]) {
    DataUtils.create(priorities).reconcileInfluentialFactor(influentialFactor);
    this._prioritiesBS.next(priorities);
  }
}
